import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { LoadingPage } from './components/ui/Loading';
import ProtectedRoute from './components/guards/ProtectedRoute';
import GuestRoute from './components/guards/GuestRoute';
import JoinContestModal from './components/contest/JoinContestModal';
import AuthModal from './components/auth/AuthModal';
import MaintenancePage from './pages/MaintenancePage';
import { useMaintenance } from './hooks/queries/useMaintenance';

// Layouts
import MainLayout from './components/layout/MainLayout';
import AuthLayout from './components/layout/AuthLayout';

// Website Pages
import LandingPage from './pages/LandingPage';
import WebsiteLandingPage from './pages/WebsiteLandingPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsPage from './pages/TermsPage';
import FAQPage from './pages/FAQPage';
import HowToPlayPage from './pages/HowToPlayPage';
import AppRedirectPage from './pages/AppRedirectPage';

// Auth Pages
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'));
const VerifyEmailPage = lazy(() => import('./pages/auth/VerifyEmailPage'));
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage'));
const GoogleCallbackPage = lazy(() => import('./pages/auth/GoogleCallbackPage'));
const CompleteProfilePage = lazy(() => import('./pages/auth/CompleteProfilePage'));
const JoinByCodePage = lazy(() => import('./pages/JoinByCodePage'));

// App Pages (lazy)
const DashboardPage = lazy(() => import('./pages/app/DashboardPage'));
const SeriesDetailPage = lazy(() => import('./pages/app/SeriesDetailPage'));
const ContestDetailPage = lazy(() => import('./pages/app/ContestDetailPage'));
const CreateContestPage = lazy(() => import('./pages/app/CreateContestPage'));
const JoinContestPage = lazy(() => import('./pages/app/JoinContestPage'));
const TeamBuilderPage = lazy(() => import('./pages/app/TeamBuilderPage'));
const EditRulesPage = lazy(() => import('./pages/app/EditRulesPage'));
const LeaderboardPage = lazy(() => import('./pages/app/LeaderboardPage'));
const TransferPage = lazy(() => import('./pages/app/TransferPage'));
const MyContestsPage = lazy(() => import('./pages/app/MyContestsPage'));
const ProfilePage = lazy(() => import('./pages/app/ProfilePage'));
const CreditsPage = lazy(() => import('./pages/app/CreditsPage'));
const TransactionsPage = lazy(() => import('./pages/app/TransactionsPage'));
const SettingsPage = lazy(() => import('./pages/app/SettingsPage'));
const AuctionRoomPage = lazy(() => import('./pages/app/AuctionRoomPage'));

// Local development hosts that should never see the maintenance gate
// even when the global flag is on — devs need to keep working while
// the prod app is paused. Production deploys never match this list.
const isLocalDev = (() => {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1' || host === '0.0.0.0';
})();

const App: React.FC = () => {
  // Top-level maintenance gate. AppSettings.maintenance.isEnabled is
  // polled every 60s; when an admin flips it on, the entire app
  // collapses to the MaintenancePage. When they flip it off, the
  // MaintenancePage unmounts and routes resume.
  const { data: maintenance } = useMaintenance();
  if (maintenance?.isEnabled && !isLocalDev) {
    return <MaintenancePage onResolved={() => { /* no-op — refetch flips the flag */ }} />;
  }

  return (
    <div className="min-h-screen bg-[#0F1114] text-gray-100">
      <Suspense fallback={<LoadingPage message="Loading..." />}>
        <Routes>
          {/* Public / Marketing */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/phone" element={<WebsiteLandingPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/how-to-play" element={<HowToPlayPage />} />
          <Route path="/app" element={<AppRedirectPage />} />

          {/* Auth — only email-flow pages that need standalone routes */}
          <Route element={<GuestRoute><AuthLayout /></GuestRoute>}>
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
          </Route>

          {/* Google OAuth callback */}
          <Route path="/auth/google/callback" element={<GoogleCallbackPage />} />
          <Route path="/complete-profile" element={<CompleteProfilePage />} />

          {/* Join contest by invite code (handles both auth states) */}
          <Route path="/join/:code" element={<JoinByCodePage />} />

          {/* Redirect old /login and /register to landing */}
          <Route path="/login" element={<Navigate to="/" />} />
          <Route path="/register" element={<Navigate to="/" />} />

          {/* Protected App Routes (MainLayout) */}
          <Route element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
            <Route path="/home" element={<DashboardPage />} />
            <Route path="/series/:id" element={<SeriesDetailPage />} />
            <Route path="/contest/create" element={<CreateContestPage />} />
            <Route path="/contest/join" element={<JoinContestPage />} />
            <Route path="/contest/:id" element={<ContestDetailPage />} />
            <Route path="/contest/:id/team/create" element={<TeamBuilderPage />} />
            <Route path="/contest/:id/team/:teamId/edit" element={<TeamBuilderPage />} />
            <Route path="/contest/:id/edit-rules" element={<EditRulesPage />} />
            <Route path="/contest/:id/leaderboard" element={<LeaderboardPage />} />
            <Route path="/contest/:id/transfers" element={<TransferPage />} />
            <Route path="/leaderboard" element={<LeaderboardPage />} />
            <Route path="/my-contests" element={<MyContestsPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/credits" element={<CreditsPage />} />
            <Route path="/transactions" element={<TransactionsPage />} />
            <Route path="/settings" element={<SettingsPage />} />
          </Route>

          {/* Auction - Full screen, no MainLayout but still protected */}
          <Route
            path="/contest/:id/auction"
            element={
              <ProtectedRoute>
                <AuctionRoomPage />
              </ProtectedRoute>
            }
          />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Suspense>
      <AuthModal />
      <JoinContestModal />
    </div>
  );
};

export default App;
